// Supporters et encouragements (D-066, Idries : « prévenir qui vient voir les matchs des autres
// équipes, mettre des mots d'encouragement »). Sur n'importe quel match du club, tout compte du
// club dit « Je viens voir » et laisse un mot ; l'équipe voit qui vient et lit les mots sur sa
// fiche de match (ses comptes sont prévenus, une fois par heure au plus).
import { clubMatchBoard, clubMatchEntries, deleteCheer, matchSupport, sendCheer, setComing, type BoardMatch, type MatchSupport } from '../lib/api';
import { entryFor, entryText, type MatchEntry } from '../domain/entry';
import { modOn, type Ctx } from './context';
import { time } from './format';
import { localDate } from '../domain/schedule';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { fillCrests, oppCrest } from './crest';
import { markFollowed, renderFollowGroups } from './suivre';

const DAY = 86400_000;
const isoIn = (days: number) => new Date(Date.now() + days * DAY).toISOString();
const quick = (gender: string | null) => [
  gender === 'F' ? 'Allez les filles ! 💪' : gender === 'M' ? 'Allez les gars ! 💪' : 'Allez ! 💪',
  'On croit en vous !', 'Bon match ! 🏀', 'Lâchez rien !', 'Bravo pour le match !',
];
const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? 'à l’instant' : m < 60 ? `il y a ${m} min` : m < 1440 ? `il y a ${Math.round(m / 60)} h`
    : new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};
const label = (m: BoardMatch) => `${m.team_name} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
const whoComes = (n: number, names: string[]) => (n
  ? `👥 ${n} ${n > 1 ? 'viennent' : 'vient'} : ${names.join(', ')}${n > names.length ? '…' : ''}`
  : 'Personne n’a encore dit qu’il venait');

function matchRow(m: BoardMatch, mine: { staff: Set<string>; play: Set<string> }, entries: MatchEntry[] = []): Html {
  const d = new Date(m.starts_at);
  // J'encadre l'équipe : je ne « viens pas voir ». J'y joue : la fiche du match décide
  // (pas retenue, absente ou blessée, je peux venir voir ; convoquée et présente, non).
  const own = mine.staff.has(m.team_id), play = !own && mine.play.has(m.team_id);
  const over = d.getTime() < Date.now() - 4 * 3600_000;
  const played = m.score_us != null && m.score_them != null;
  const res = m.forfeit ? (m.forfeit === 'them' ? 'win' : 'loss') : played ? (m.score_us! > m.score_them! ? 'win' : m.score_us! < m.score_them! ? 'loss' : 'draw') : null;
  return html`
    <li class="cm-row" data-ev="${m.event_id}">
      <span class="date-tile"><b>${d.getDate()}</b><small>${d.toLocaleDateString('fr-FR', { month: 'short' })}</small></span>
      <span class="cm-main">
        <span class="card-title crest-line">${oppCrest(m, 'opp-crest-xs')}<span>${label(m)}</span></span>
        <span class="muted small">${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${time(d)} · ${m.is_home === true ? 'à domicile' : m.is_home === false ? 'à l’extérieur' : 'terrain neutre'}${m.location ? ` · ${m.location}` : ''}</span>
        ${(() => { const t = m.is_home === true && !over ? entryText(entryFor(entries, m)) : null; return t ? html`<span class="small cm-entry">${t}</span>` : ''; })()}
        <span class="small cm-who">${whoComes(m.supporters, m.supporter_names)}</span>
      </span>
      <span class="cm-acts">
        ${over ? (res ? html`<b class="result-score ${res === 'win' ? 'win' : res === 'loss' ? 'loss' : ''}">${m.forfeit ? (res === 'win' ? 'V' : 'D') : `${m.score_us} – ${m.score_them}`}</b>` : '')
          : own ? html`<span class="chip chip-mute">Ton équipe</span>` : play ? html`<a class="chip chip-mute" href="#/e/${m.event_id}/${localDate(d)}">Ton match ›</a>` : html`<button type="button" class="cm-go ${m.me_coming ? 'on' : ''}" data-coming="${m.event_id}" aria-pressed="${String(m.me_coming)}">${m.me_coming ? '✓ J’y serai' : 'Je viens voir'}</button>`}
        <button type="button" class="cm-cheer" data-guide="cm-cheer" data-cheer="${m.event_id}">📣 ${over ? 'Féliciter' : 'Encourager'}${m.cheers ? ` · ${m.cheers}` : ''}</button>
      </span>
    </li>`;
}

/** Les équipes que j'entraîne et celles où je joue (moi, pas mes enfants), toutes casquettes. Le bureau
 *  (droits sur toutes les équipes) vient voir les matchs comme tout le monde (D-066, casquette Bénévole D-097). */
const myTeams = (ctx: Ctx) => ({
  staff: new Set(ctx.full.pureCoach),
  play: new Set(ctx.full.memberships.filter(m => ctx.full.people.some(p => p.id === m.person_id && p.user_id === ctx.uid)).map(m => m.team_id)),
});

function wire(root: HTMLElement, ctx: Ctx, list: BoardMatch[], reload: () => void): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-coming]')) b.addEventListener('click', async () => {
    const on = b.getAttribute('aria-pressed') !== 'true';
    b.disabled = true;
    try { await setComing(b.dataset.coming!, on); toast(on ? 'C’est noté : l’équipe sait que tu viens' : 'C’est noté'); reload(); }
    catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-cheer]')) b.addEventListener('click', () => {
    const m = list.find(x => x.event_id === b.dataset.cheer);
    if (m) void openCheers(ctx, m.event_id, label(m), m.gender, reload);
  });
}

/** Écrire un mot (raccourcis), lire ceux des autres, effacer le sien (ou, pour le staff, un mot déplacé). */
export async function openCheers(_ctx: Ctx, eventId: string, title: string, gender: string | null, onDone: () => void): Promise<void> {
  let s: MatchSupport | null = await matchSupport(eventId).catch(() => null);
  openSheet(`Encourager · ${title}`, html`
    <form class="form" id="f-cheer" novalidate>
      <div class="chip-scroll" role="group" aria-label="Mots tout prêts">${quick(gender).map(q => html`<button type="button" class="chip-toggle" data-q="${q}">${q}</button>`)}</div>
      <label class="field"><span>Ton mot (l’équipe le lit sur sa fiche de match)</span><textarea name="body" rows="3" maxlength="280" placeholder="ex. On sera dans les tribunes, allez !"></textarea></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Envoyer</button>
    </form>
    <div id="cheer-list"></div>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-cheer')!;
    const ta = f.elements.namedItem('body') as HTMLTextAreaElement;
    const draw = () => {
      const list = s?.cheers ?? [];
      mount($(sheet, '#cheer-list')!, list.length ? html`<ul class="cheer-wall">${list.map(c => html`<li>
        <span class="cheer-body">« ${c.body} »</span><span class="muted small">${c.name} · ${ago(c.at)}</span>
        ${c.can_delete ? html`<button type="button" class="link-x" data-del="${c.id}" aria-label="Effacer ce mot">effacer</button>` : ''}</li>`)}</ul>` : html``);
      for (const b of $$<HTMLButtonElement>(sheet, '[data-del]')) b.addEventListener('click', async () => {
        try { await deleteCheer(b.dataset.del!); s = await matchSupport(eventId).catch(() => s); draw(); onDone(); } catch { toast('Impossible pour le moment.', 'ko'); }
      });
    };
    draw();
    for (const b of $$<HTMLButtonElement>(sheet, '[data-q]')) b.addEventListener('click', () => { ta.value = b.dataset.q!; ta.focus(); });
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const body = ta.value.trim();
      if (!body) { $(f, '.form-msg')!.textContent = 'Écris un mot, ou choisis-en un.'; return; }
      try { await sendCheer({ event_id: eventId, body }); close(); toast('Merci ! L’équipe est prévenue'); onDone(); }
      catch (err) { $(f, '.form-msg')!.textContent = String((err as Error).message).includes('trop') ? 'Beaucoup de mots en peu de temps : réessaie dans un moment.' : 'Envoi impossible.'; }
    });
  });
}

/** Les matchs de tout le club, dans l'écran Matchs (D-132, filtre « Tout le club ») : « Suivre » et ★ (D-110), à venir
 *  (3 semaines) et résultats récents, « Je viens voir », encourager (D-066). `onView` : le segment choisi. */
export async function renderClubBoard(root: HTMLElement, ctx: Ctx, view: 'next' | 'past', onView: (v: 'next' | 'past') => void): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const [from, to] = view === 'next' ? [isoIn(-4 / 24), isoIn(21)] : [isoIn(-21), isoIn(-4 / 24)];
  const list = view === 'next' ? await clubMatchBoard(club.id, from, to) : (await clubMatchBoard(club.id, from, to)).reverse();
  const reload = () => void renderClubBoard(root, ctx, view, onView);
  const mine = myTeams(ctx);
  const entries = await clubMatchEntries({ club: club.id }).catch(() => [] as MatchEntry[]);   // D-106
  const follow = modOn(ctx, 'page');
  mount(root, html`
    ${follow ? html`<div id="fw-groups"></div>` : ''}
    <div class="seg" role="tablist" aria-label="Période">
      <button type="button" role="tab" data-view="next" aria-selected="${String(view === 'next')}">À venir</button>
      <button type="button" role="tab" data-view="past" aria-selected="${String(view === 'past')}">Résultats</button>
    </div>
    ${list.length ? html`<ul class="cm-list">${list.map(m => matchRow(m, mine, entries))}</ul>`
      : html`<div class="empty"><p class="empty-title">${view === 'next' ? 'Pas de match dans les 3 semaines.' : 'Pas de résultat récent.'}</p></div>`}`);
  for (const b of $$<HTMLButtonElement>(root, '[data-view]')) b.addEventListener('click', () => onView(b.dataset.view === 'past' ? 'past' : 'next'));
  wire(root, ctx, list, reload);
  void fillCrests(root);
  if (follow) {
    void renderFollowGroups($(root, '#fw-groups')!, club.id, () => void markFollowed(root, club.id, from, to));
    void markFollowed(root, club.id, from, to);
  }
}

/** Carte d'accueil : les prochains matchs du club (sauf ceux de mes équipes), « Je viens voir » ; 3 (`max`), le reste dans Matchs. */
export async function renderClubMatchesCard(el: HTMLElement, ctx: Ctx, opts: { exclude?: Set<string>; title?: string; max?: number } = {}): Promise<void> {
  if (!ctx.club) return;
  const list = (await clubMatchBoard(ctx.club.id, isoIn(-3 / 24), isoIn(8)).catch(() => [] as BoardMatch[]))
    .filter(m => !opts.exclude?.has(m.team_id)).slice(0, opts.max ?? 3);
  const mine = myTeams(ctx);
  if (!list.length) { mount(el, html``); return; }
  mount(el, html`
    <section class="sec">
      <div class="sec-head"><h3 class="sec-title">${opts.title ?? 'Viens encourager les autres équipes'}</h3><a class="sec-link" href="#/matchs">Tous →</a></div>
      <ul class="cm-list">${list.map(m => matchRow(m, mine))}</ul>
    </section>`);
  wire(el, ctx, list, () => void renderClubMatchesCard(el, ctx, opts));
  void fillCrests(el);
}

/** Sur la fiche de match de l'équipe : qui vient, et le mur d'encouragements. */
export async function renderMatchSupport(el: HTMLElement, ctx: Ctx, eventId: string, title: string, gender: string | null, playing: boolean): Promise<void> {
  const s = await matchSupport(eventId).catch(() => null);
  // Pas de bloc supporters vide (D-121) : pour qui joue le match (le staff, les convoqués), rien tant que personne ne vient.
  if (!s || (playing && !s.supporters.length && !s.cheers.length)) { mount(el, html``); return; }
  const reload = () => void renderMatchSupport(el, ctx, eventId, title, gender, playing);
  mount(el, html`
    <section class="block cheer-block">
      <div class="sec-head"><h3>Supporters</h3>
        <button type="button" class="btn btn-ghost btn-sm" id="ms-cheer">📣 ${s.open ? 'Encourager' : 'Féliciter'}</button></div>
      ${s.supporters.length ? html`<p class="small">${whoComes(s.supporters.length, s.supporters.slice(0, 6))}</p>` : html`<p class="small muted">Tu viens voir le match ? Dis-le, l’équipe aime le savoir.</p>`}
      ${s.cheers.length ? html`<ul class="cheer-wall">${s.cheers.slice(0, 5).map(c => html`<li><span class="cheer-body">« ${c.body} »</span><span class="muted small">${c.name} · ${ago(c.at)}</span></li>`)}</ul>` : ''}
      ${s.cheers.length > 5 ? html`<button type="button" class="btn btn-link btn-sm" id="ms-all">Les ${s.cheers.length} mots</button>` : ''}
      ${s.open && !playing ? html`<button type="button" class="cm-go ${s.me ? 'on' : ''}" id="ms-go" aria-pressed="${String(s.me)}">${s.me ? '✓ J’y serai' : 'Je viens voir'}</button>` : ''}
    </section>`);
  const open = () => void openCheers(ctx, eventId, title, gender, reload);
  $(el, '#ms-cheer')?.addEventListener('click', open);
  $(el, '#ms-all')?.addEventListener('click', open);
  $(el, '#ms-go')?.addEventListener('click', async () => {
    try { await setComing(eventId, !s.me); reload(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
}
